import React, { useEffect, useState } from "react";
import { Activity, Loader2 } from "lucide-react";
import { fetchHeatmap } from "@/lib/api";
import { fmtPct, pctColor } from "./helpers";

const HeatTile = ({ symbol, label, pct, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={`heat-tile-${symbol}`}
    className="relative rounded-md border border-white/10 px-2 py-2.5 text-left transition hover:border-white/30 hover:scale-[1.02] focus:outline-none focus:ring-1 focus:ring-[#3FE5DC]"
    style={{
      background: pctColor(pct),
      boxShadow:
        pct != null && Math.abs(pct) > 1.5
          ? `0 0 12px ${pct >= 0 ? "rgba(0,230,120,0.35)" : "rgba(255,70,80,0.35)"}`
          : "none",
    }}
  >
    <div className="text-[11.5px] font-semibold tracking-tight text-white num truncate">
      {symbol}
    </div>
    {label && (
      <div className="text-[9px] uppercase tracking-[0.16em] text-white/75 truncate">{label}</div>
    )}
    <div
      className="text-[12.5px] font-bold num text-white mt-0.5"
      style={{ textShadow: "0 0 6px rgba(0,0,0,0.55)" }}
    >
      {fmtPct(pct)}
    </div>
  </button>
);

export const NeonHeatmap = ({ onPick }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHeatmap()
      .then(setData)
      .catch(() => setData({ sectors: [], movers: [] }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="rounded-xl border border-white/10 bg-[#070710] p-5 mb-6 flex items-center gap-2 text-[12px] text-white/45">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading market heatmap…
      </div>
    );
  }
  if (!data?.sectors?.length && !data?.movers?.length) return null;

  return (
    <div
      className="rounded-xl border border-white/10 bg-[#070710] p-4 sm:p-5 mb-6"
      data-testid="analysis-heatmap"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-[#3FE5DC]" />
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#3FE5DC]">
            Market heatmap · live
          </span>
        </div>
        <span className="text-[10px] uppercase tracking-[0.18em] text-white/35">
          Tap any tile to analyze
        </span>
      </div>
      <div className="mb-2 text-[10px] uppercase tracking-[0.18em] text-white/55">Sectors</div>
      <div className="grid grid-cols-3 sm:grid-cols-6 lg:grid-cols-11 gap-2 mb-4">
        {data.sectors.map((s) => (
          <HeatTile
            key={s.symbol}
            symbol={s.symbol}
            label={s.label}
            pct={s.change_pct}
            onClick={() => onPick?.(s.symbol)}
          />
        ))}
      </div>
      <div className="mb-2 text-[10px] uppercase tracking-[0.18em] text-white/55">
        Movers · top by |% change|
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-10 gap-2">
        {data.movers.slice(0, 20).map((m) => (
          <HeatTile
            key={m.symbol}
            symbol={m.symbol}
            pct={m.change_pct}
            onClick={() => onPick?.(m.symbol)}
          />
        ))}
      </div>
    </div>
  );
};
